/**
 * RBAC catalog — the single source of truth for permissions and the built-in
 * roles. Both the API (enforcement) and the web app (UI gating) import these,
 * so the two can never drift apart.
 *
 * Model: fine-grained `resource:action` permissions, grouped into roles.
 * A user may hold MANY roles; their effective permission set is the union.
 * The database is authoritative at runtime (roles/permissions are stored and
 * can be edited by admins) — this file defines the *built-in* baseline that the
 * seed script installs and that the type system understands.
 */

export const PERMISSIONS = [
  // User management
  "user:read",
  "user:create",
  "user:update",
  "user:delete",
  // Role / permission administration
  "role:read",
  "role:create",
  "role:update",
  "role:delete",
  "role:assign",
  // Sessions & security
  "session:read",
  "session:revoke",
  "mfa:manage",
  // Audit & compliance
  "audit:read",
  "audit:export",
  // System / operations
  "system:read",
  "system:admin",
  // DPP domain (products, passports, organizations, imports)
  "product:read",
  "product:write",
  "passport:read",
  "passport:write",
  "passport:publish",
  "org:read",
  "org:manage",
  "import:run",
  // Supplier data collection portal (invites, submission review/apply).
  // Deliberately NOT folded into `import:run`: soliciting data from external
  // parties (and applying it) is a different trust decision than running a CSV
  // import, and operators must be able to grant/revoke them independently.
  "supplier:manage",
  // Restricted-tier access grants (EN 18239 legitimate-interest parties).
  // Admin-only on purpose: issuing restricted-data access to EXTERNAL parties
  // (repairers, recyclers, customs) is a higher-trust act than editing products
  // or soliciting supplier data — editors deliberately lack it.
  "access:manage",
  // Machine-to-machine integration (API keys, webhooks). Admin-only on purpose:
  // an API key is a long-lived credential that bypasses user RBAC/MFA entirely
  // (its scopes are its sole authority), and a webhook exfiltrates org events to
  // an external URL — both are credential-issuance acts, not content edits, so
  // editors deliberately lack it (access:manage rationale).
  "integration:manage",
  // EU DPP Registry economic-operator enrolment (ESPR Art. 13). Admin-only on
  // purpose: this declares the org's LEGAL identity to the Commission (legal
  // name, registration identifier, compliance contact) and gates whether the
  // org may register passports at all — a corporate-identity act, not content.
  "registry:manage",
  // Unsold-goods destruction disclosure (draft TR ESPR Art. 24–25 / ESPR Art.
  // 24–25): a public annual statement in the org's name. Admin-only on purpose —
  // it is a corporate disclosure to the public and the authority, not content.
  "disclosure:manage",
] as const;

/**
 * Permission keys that once shipped but were renamed or removed. The seed /
 * bootstrap deletes these rows (role bindings cascade) so the DB catalog can
 * never drift from the live PERMISSIONS list. Keys stay here forever.
 */
export const RETIRED_PERMISSIONS = [
  // Early DPP permissions used dot separators; renamed to `resource:action`.
  "product.read",
  "product.write",
  "passport.read",
  "passport.write",
  "passport.publish",
  "org.read",
  "org.manage",
  "import.run",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export const PERMISSION_SET: ReadonlySet<Permission> = new Set(PERMISSIONS);

export function isPermission(value: string): value is Permission {
  return PERMISSION_SET.has(value as Permission);
}

/** Wildcard meaning "every permission" — held only by the superadmin role. */
export const ALL_PERMISSIONS = "*" as const;

export interface RoleDefinition {
  readonly name: string;
  readonly description: string;
  /** Either the wildcard or an explicit permission list. */
  readonly permissions: readonly Permission[] | typeof ALL_PERMISSIONS;
  /** System roles cannot be deleted or renamed via the API. */
  readonly system: true;
}

export const BUILTIN_ROLES = {
  superadmin: {
    name: "superadmin",
    description: "Unrestricted access. Break-glass / platform owner. Requires MFA.",
    permissions: ALL_PERMISSIONS,
    system: true,
  },
  admin: {
    name: "admin",
    description: "Manages users, roles, sessions and reads audit logs.",
    permissions: [
      "user:read",
      "user:create",
      "user:update",
      "user:delete",
      "role:read",
      "role:assign",
      "session:read",
      "session:revoke",
      "mfa:manage",
      "audit:read",
      "system:read",
      "product:read",
      "product:write",
      "passport:read",
      "passport:write",
      "passport:publish",
      "org:read",
      "org:manage",
      "import:run",
      "supplier:manage",
      "access:manage",
      "integration:manage",
      "registry:manage",
      "disclosure:manage",
    ],
    system: true,
  },
  editor: {
    name: "editor",
    description: "Creates and edits products/passports within their organizations. Cannot publish.",
    permissions: [
      "product:read",
      "product:write",
      "passport:read",
      "passport:write",
      "import:run",
      "supplier:manage",
      "org:read",
    ],
    system: true,
  },
  viewer: {
    name: "viewer",
    description: "Read-only access to products and passports within their organizations.",
    permissions: ["product:read", "passport:read", "org:read"],
    system: true,
  },
  auditor: {
    name: "auditor",
    description: "Read-only access to users and the full audit trail. Cannot mutate.",
    permissions: ["user:read", "role:read", "session:read", "audit:read", "audit:export", "system:read"],
    system: true,
  },
  user: {
    name: "user",
    description:
      "Standard authenticated user. Holds NO collection-level permissions; " +
      "self-service endpoints (/auth/me, password, MFA) are gated by authentication only.",
    permissions: [],
    system: true,
  },
} as const satisfies Record<string, RoleDefinition>;

export type BuiltinRoleName = keyof typeof BUILTIN_ROLES;

/* --- Per-organization roles (tenant-scoped RBAC) --------------------------- */

/**
 * Org-level roles. Unlike the global {@link BUILTIN_ROLES} (which govern the
 * platform surface — user/role/audit/system admin), these govern what a member
 * may do *inside one organization*. A user can hold different org roles in
 * different orgs (admin in A, viewer in B); the effective org permissions are
 * derived per-request from the membership row, never from the JWT.
 *
 * "owner" from the old two-value model maps onto "admin"; "member" maps onto
 * editor/viewer at migration time (see migration 0017).
 */
export const ORG_ROLES = ["admin", "editor", "viewer"] as const;
export type OrgRole = (typeof ORG_ROLES)[number];

/**
 * The subset of {@link PERMISSIONS} that is meaningful *within* a tenant and is
 * therefore granted by an org role rather than by a global role. Everything not
 * listed here (user:*, role:*, session:*, audit:*, system:*, org:manage) is a
 * PLATFORM permission that stays on the global JWT `perms` and is checked with
 * `authorize`, not `authorizeOrg`.
 */
export const ORG_SCOPED_PERMISSIONS = [
  "product:read",
  "product:write",
  "passport:read",
  "passport:write",
  "passport:publish",
  "org:read",
  "import:run",
  "supplier:manage",
  "access:manage",
  "integration:manage",
  "registry:manage",
  "disclosure:manage",
] as const satisfies readonly Permission[];

export type OrgScopedPermission = (typeof ORG_SCOPED_PERMISSIONS)[number];

/**
 * Org role → permission set. Mirrors the DPP slice of the built-in global roles
 * so behaviour is unchanged for a given role name: an org "editor" can write
 * products/passports and run imports but cannot publish, issue restricted-tier
 * access, or manage machine integrations — those stay with the org "admin".
 */
export const ORG_ROLE_PERMISSIONS: Readonly<Record<OrgRole, readonly OrgScopedPermission[]>> = {
  admin: [...ORG_SCOPED_PERMISSIONS],
  editor: [
    "product:read",
    "product:write",
    "passport:read",
    "passport:write",
    "import:run",
    "supplier:manage",
    "org:read",
  ],
  viewer: ["product:read", "passport:read", "org:read"],
};

/** Effective org permissions for a role, as a set for O(1) checks. */
export function orgRolePermissions(role: OrgRole): ReadonlySet<OrgScopedPermission> {
  return new Set(ORG_ROLE_PERMISSIONS[role]);
}

/** Full org-permission set — used for the platform-admin cross-tenant bypass. */
export function allOrgPermissions(): ReadonlySet<OrgScopedPermission> {
  return new Set(ORG_SCOPED_PERMISSIONS);
}

/** Authorize an org-scoped action against a member's effective org permissions. */
export function canOrg(effective: ReadonlySet<OrgScopedPermission>, required: OrgScopedPermission): boolean {
  return effective.has(required);
}

/** Whether a permission is one an org role can grant (vs. a platform permission). */
export function isOrgScopedPermission(p: Permission): p is OrgScopedPermission {
  return (ORG_SCOPED_PERMISSIONS as readonly Permission[]).includes(p);
}

/** Resolve a set of role permission-lists into an effective permission set. */
export function effectivePermissions(
  roles: ReadonlyArray<{ permissions: readonly string[] | typeof ALL_PERMISSIONS }>,
): ReadonlySet<Permission | typeof ALL_PERMISSIONS> {
  const set = new Set<Permission | typeof ALL_PERMISSIONS>();
  for (const role of roles) {
    if (role.permissions === ALL_PERMISSIONS) {
      set.add(ALL_PERMISSIONS);
      continue;
    }
    for (const p of role.permissions) {
      if (isPermission(p)) set.add(p);
    }
  }
  return set;
}

/** Authorize: does this effective permission set satisfy the required permission? */
export function can(
  effective: ReadonlySet<Permission | typeof ALL_PERMISSIONS>,
  required: Permission,
): boolean {
  return effective.has(ALL_PERMISSIONS) || effective.has(required);
}
